import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import GoogleMapReact from 'google-map-react';
import { Link } from 'react-router-dom';
import { PiBuildingsDuotone, PiCaretDownBold, PiChatCircleTextDuotone, PiClockDuotone, PiEnvelopeSimpleDuotone, PiFacebookLogoDuotone, PiMapPinDuotone, PiPaperPlaneTiltDuotone, PiPhoneDuotone, PiShieldCheckDuotone, PiUserDuotone, } from 'react-icons/pi';

import ExpandableCardList from 'elements/ExpandableCardList';
import Pattern from 'elements/Pattern';
import PageHeading from 'elements/PageHeading';
import FeedbackModal from 'components/FeedbackModal';

import { useConfig } from 'providers/ConfigProvider';

const MAP_API_KEY = 'AIzaSyCViy-89sjbJvf4buFYen5k6yAO3exuoUw';
const mapOptions = { styles: [{ featureType: 'all', elementType: 'all', stylers: [{ saturation: -100 }] }], fullscreenControl: false, streetViewControl: false, mapTypeControl: false, };

const SmallMarker = () => <PiMapPinDuotone className='size-7 text-neutral-700' />;

export default function ContactPage(props) {
    return (
        <div className={' w-full flex-1 flex flex-col items-center ' + (props.className || '')}>

            <div className='relative w-full flex flex-col items-center gradient-green px-site py-site pb-8 lg:pb-12 xl:pb-24 rounded-b-3xl z-20 overflow-hidden'>

                <Pattern />

                <div className='w-full max-w-boxed flex flex-col gap-12 md:gap-16 z-10'>

                    <PageHeading title='Irodák elérhetőségei' subTitle='Válaszd ki a hozzád legközelebbi irodánkat, vagy írj nekünk az űrlapon. Kollégáink munkanapokon 9 és 17 óra között válaszolnak.' />

                    <OfficeList />

                </div>

            </div>


            <div className='relative w-full flex flex-col items-center gradient-yellow px-site  pb-8 lg:pb-12 xl:pb-16 -mt-8 pt-16 lg:pt-24 xl:pt-32 z-10'>

                <Pattern />

                <div className='w-full max-w-boxed flex flex-col gap-12 md:gap-16 z-10'>

                    <PageHeading title='Írj nekünk, hogy segíteni tudjunk!' subTitle='Kérdésed van? Add meg az elérhetőségeidet, hogy segíteni tudjunk.' />

                    <Form />

                </div>

            </div>

        </div>
    );
}

function OfficeList() {
    const { comboData } = useConfig();
    const offices = comboData?.office || [];
    const items = offices.map((office) => ({
        id: office.id,
        title: office.name,
        titleClassName: office.name?.length > 20 ? 'text-lg sm:text-xl xl:text-3xl' : 'text-2xl  sm:text-3xl',
        hideContentTitle: true,
        contentClassName: 'overflow-hidden',
        description: <OfficeSubtitle office={office} />,
        // Icon: PiBuildingsDuotone,
        data: office,
    }));

    return (
        <section className='flex flex-col gap-6 md:gap-8'>
            <ExpandableCardList items={items} renderContent={renderOfficeContent} />
        </section>
    );
}

function OfficeSubtitle({ office }) {
    const details = [
        { value: [office.zipcode, office.address].filter(Boolean).join(' '), Icon: PiMapPinDuotone, },
        { value: office.phones, Icon: PiPhoneDuotone },
        { value: office.emails, Icon: PiEnvelopeSimpleDuotone },
        // { value: office.openingHours, Icon: PiClockDuotone, isHtml: true },
    ].filter(({ value }) => value);

    return <span className='flex flex-wrap items-center gap-x-2 gap-y-1'>
        {details.map(({ value, Icon, isHtml }, index) => <span key={`${value}-${index}`} className='inline-flex min-w-0 items-center gap-1.5'>
            {index > 0 && <span aria-hidden='true' className='text-neutral-300'>•</span>}
            <Icon className='size-4 shrink-0 text-neutral-400' />
            {isHtml
                ? <span className='break-words [&_br]:hidden [&_p]:inline' dangerouslySetInnerHTML={{ __html: value }} />
                : <span className='break-words'>{value}</span>}
        </span>)}
    </span>;
}

function renderOfficeContent({ data }) {
    return <OfficeDetails data={data} />;
}

function OfficeDetails({ data }) {
    const [coordinates, setCoordinates] = useState({ lat: 47.4979, lng: 19.0402 });

    useEffect(() => {
        const fetchCoordinates = async () => {
            try {
                const address = encodeURIComponent(`${data.zipcode} ${data.name}, ${data.address}`);
                const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${MAP_API_KEY}`);
                const locationData = await response.json();
                const location = locationData.results?.[0]?.geometry?.location;
                if (location) setCoordinates({ lat: location.lat, lng: location.lng });
            } catch (error) {
                console.error('Error fetching geocode data:', error);
            }
        };
        fetchCoordinates();
    }, [data.address, data.name, data.zipcode]);

    return <div className='flex flex-col bg-white'>
        <div className='grid grid-cols-1 lg:grid-cols-2 lg:items-stretch'>
            <div className='relative min-h-[300px] overflow-hidden rounded-3xl bg-neutral-100 shadow-2xl sm:min-h-[380px] lg:min-h-[480px]'>
                <GoogleMapReact bootstrapURLKeys={{ key: MAP_API_KEY }} center={coordinates} defaultZoom={15} options={mapOptions}>
                    <SmallMarker lat={coordinates.lat} lng={coordinates.lng} />
                </GoogleMapReact>
                <h2 className={`office-map-title pointer-events-none absolute left-0 top-0 z-10 max-w-[85%] bg-white shadow-inner px-5 py-4 font-display font-bold leading-tight text-neutral-900 sm:px-6 sm:py-5 ${data.name?.length > 20 ? 'text-base sm:text-xl' : 'text-xl sm:text-3xl'}`}>
                    <span>{data.name}</span>
                </h2>
                <div className='pointer-events-none absolute bottom-4 right-4 z-10 flex max-w-[85%] items-start gap-2 rounded-2xl gradient-yellow p-3 text-white shadow-xl sm:bottom-5 sm:right-5 sm:max-w-[70%]'>
                    <PiMapPinDuotone className='mt-0.5 size-5 shrink-0' />
                    <div>
                        <div className='text-[9px] font-bold uppercase tracking-[0.14em] text-white/65'>Iroda címe</div>
                        <div className='font-display text-xs font-bold leading-snug sm:text-sm'>{data.zipcode} {data.name}, {data.address}</div>
                    </div>
                </div>
            </div>

            <div className='flex min-w-0 flex-col gap-3 bg-white p-4 sm:p-5'>
                {data.siteNote && <OfficeNeutralCard Icon={PiMapPinDuotone} label='Megközelítés'>
                    {data.siteNote}
                </OfficeNeutralCard>}

                {data.openingHours && <OfficeNeutralCard Icon={PiClockDuotone} label='Nyitvatartás'>
                    <div className='[&_div]:inline [&_div+div]:before:mx-1.5 [&_div+div]:before:content-["•"]' dangerouslySetInnerHTML={{ __html: data.openingHours }} />
                </OfficeNeutralCard>}

                <div className='grid grid-cols-1 gap-3'>

                    <div className='min-w-0 flex flex-col xl:flex-row gap-3'>
                        {data.phones && <OfficeInfoCard to={`tel:${data.phones}`} Icon={PiPhoneDuotone} label='Telefon' className='flex-1 gradient-green'>
                            {data.phones}
                        </OfficeInfoCard>}

                        {data.emails && <OfficeInfoCard to={`mailto:${data.emails}`} Icon={PiEnvelopeSimpleDuotone} label='E-mail' className='flex-1 gradient-yellow'>
                            <span className='break-all'>{data.emails}</span>
                        </OfficeInfoCard>}

                        {data.facebookUrl && <>
                            <OfficeInfoCard to={data.facebookUrl} target='_blank' rel='noopener noreferrer' Icon={PiFacebookLogoDuotone} label='Facebook' className='gradient-blue xl:hidden'>
                                <span className='truncate'>Kövesd irodánkat Facebookon is!</span>
                            </OfficeInfoCard>
                            <Link to={data.facebookUrl} target='_blank' rel='noopener noreferrer' aria-label='Kövesd irodánkat Facebookon' title='Facebook' className='aspect-square group hidden items-center justify-center rounded-2xl gradient-blue text-white shadow-sm transition hover:-translate-y-0.5 hover:brightness-110 hover:shadow-md xl:flex'>
                                <PiFacebookLogoDuotone className='size-6 transition-transform group-hover:scale-110' />
                            </Link>
                        </>}
                    </div>

                </div>

                {data.users?.length > 0 && <section className='mt-1 flex flex-col gap-2.5 bg-neutral-50 p-3 rounded-2xl'>
                    <div className='flex items-center gap-2 font-display text-sm font-bold text-neutral-900'>
                        <PiUserDuotone className='size-4 text-neutral-500' />
                        Kollégáink
                    </div>
                    <div className='grid grid-cols-1 gap-2.5 sm:grid-cols-2'>
                        {data.users.map((user, index) => <OfficeUser key={user.id || user.email || index} user={user} />)}
                    </div>
                </section>}
            </div>
        </div>
    </div>;
}

function OfficeInfoCard({ to, target, rel, Icon, label, className = '', children }) {
    return <Link to={to} target={target} rel={rel} className={`block min-w-0 rounded-2xl p-2 text-white shadow-sm transition hover:-translate-y-0.5 hover:brightness-110 hover:shadow-md ${className}`}>

        <div className='flex items-center gap-3'>
            <div className='aspect-square flex h-9 w-9 items-center justify-center rounded-lg bg-white/20 shadow-inner'>
                <Icon className='size-6 shrink-0 text-white/75' />
            </div>

            <div className='flex flex-col gap-0'>
                <div className='mb-0.5 flex items-center gap-2 text-xs text-white/75 leading-none'>{label}</div>

                <div className='min-w-0 text-sm font-semibold'>{children}</div>
            </div>

        </div>
    </Link>;
}

function OfficeNeutralCard({ Icon, label, children }) {
    return <div className='rounded-2xl bg-neutral-100 p-3 text-[11px] leading-relaxed text-neutral-600'>
        <div className='mb-1 flex items-center gap-1 font-display text-xs font-bold text-neutral-900'>
            <Icon className='size-3.5 shrink-0 text-neutral-500' />
            {label}
        </div>
        {children}
    </div>;
}

function OfficeUser({ user }) {
    const phone = `+${user.countryCode}${user.serviceProviderNumber}${user.phoneNumber}`;

    return <div className='flex min-w-0 items-center gap-2.5 rounded-2xl bg-white p-2.5 text-neutral-800 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md'>
        <img src={user.photoKey ? `https://www.furgediak.hu/furgediak-backings/methods/photoRepository/getImage?key=${user.photoKey}` : '/assets/images/avatar.png'} alt={user.name} className='size-12 shrink-0 rounded-xl object-cover sm:size-14' />
        <div className='min-w-0 flex-1'>
            <strong className='block truncate font-display text-sm font-bold text-neutral-900'>{user.name}</strong>
            <div className='truncate text-[10px] text-neutral-500'>{user.post}</div>
            <div className='mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-neutral-600'>
                <Link to={`tel:${phone}`} className='inline-flex items-center gap-1 transition hover:text-black hover:underline'>
                    <PiPhoneDuotone className='size-3.5 shrink-0 text-neutral-400' />
                    +{user.countryCode} {user.serviceProviderNumber} {user.phoneNumber}
                </Link>
                <Link to={`mailto:${user.email}`} className='inline-flex min-w-0 items-center gap-1 transition hover:text-black hover:underline'>
                    <PiEnvelopeSimpleDuotone className='size-3.5 shrink-0 text-neutral-400' />
                    <span className='truncate'>{user.email}</span>
                </Link>
            </div>
        </div>
    </div>;
}

function Form() {
    const inputClassName = 'w-full px-4 py-3.5 sm:py-4 border border-gray-300 rounded-lg outline-none text-sm text-neutral-800 placeholder:text-gray-400 transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100';
    const iconInputClassName = inputClassName + ' pl-12';
    const inputIconClassName = 'absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none';
    const selectClassName = inputClassName + ' appearance-none bg-white pr-11';
    const selectArrowClassName = 'pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-neutral-500';

    const [captchaState, setCaptchaState] = useState('idle');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        if (captchaState !== 'checking') return undefined;

        const verificationTimer = setTimeout(() => setCaptchaState('verified'), 1100);
        return () => clearTimeout(verificationTimer);
    }, [captchaState]);

    const { comboData } = useConfig();
    const offices = comboData?.office || [];

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (captchaState !== 'verified' || isSubmitting) return;

        const formData = new FormData(event.currentTarget);
        const body = {
            dto: {
                firstName: formData.get('firstName')?.trim(),
                lastName: formData.get('lastName')?.trim(),
                email: formData.get('email')?.trim(),
                message: formData.get('message')?.trim(),
                office: formData.get('office'),
                countryCode: formData.get('countryCode'),
                serviceProviderNumber: formData.get('serviceProviderNumber'),
                phoneNumber: formData.get('phoneNumber')?.trim(),
            },
        };

        setIsSubmitting(true);

        try {
            const response = await axios.post(
                global.backingsEndpoint + 'contactControl/sendContact',
                body
            );
            setFeedback(response.data?.successful ? 'success' : 'error');
        } catch (error) {
            console.error('Contact form submission failed:', error);
            setFeedback('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeFeedback = () => {
        if (feedback === 'success') {
            formRef.current?.reset();
            setCaptchaState('idle');
        }
        setFeedback(null);
    };

    return (
        <>
        <section className='mx-auto w-full max-w-3xl'>
            <form ref={formRef} className='w-full' onSubmit={handleSubmit}>
                <div className='mx-0 flex flex-col gap-4 rounded-3xl bg-white px-6 py-7 shadow-2xl sm:mx-6 sm:px-8 sm:py-8'>

                    <div className='mb-2 flex flex-col gap-2'>
                        <h2 className='font-display text-4xl font-black text-neutral-800 sm:text-5xl'>Kapcsolat</h2>
                        <div className='flex items-start gap-2 text-sm text-gray-600'>
                            <PiChatCircleTextDuotone className='mt-0.5 shrink-0 text-lg text-gray-400' />
                            <span>Kérdésed van? Add meg az elérhetőségeidet, hogy segíteni tudjunk.</span>
                        </div>
                    </div>

                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                        <div className='relative md:col-span-2'>
                            <PiBuildingsDuotone className={inputIconClassName} />
                            <select name='office' className={selectClassName + ' pl-12'} defaultValue='' required aria-label='Iroda kiválasztása'>
                                <option value='' disabled>Válassz irodát!</option>
                                {offices.map((office) => <option key={office.id} value={office.id}>{office.name}</option>)}
                            </select>
                            <PiCaretDownBold aria-hidden='true' className={selectArrowClassName} />
                        </div>

                        <div className='relative'>
                            <PiUserDuotone className={inputIconClassName} />
                            <input id='last-name' name='lastName' type='text' autoComplete='family-name' placeholder='Vezetéknév' className={iconInputClassName} required />
                        </div>
                        <div className='relative'>
                            <PiUserDuotone className={inputIconClassName} />
                            <input id='first-name' name='firstName' type='text' autoComplete='given-name' placeholder='Keresztnév' className={iconInputClassName} required />
                        </div>

                        <div className='grid grid-cols-[82px_82px_1fr] gap-2'>
                            <div className='relative'>
                                <select name='countryCode' className={selectClassName + ' px-2 pr-7 text-center'} defaultValue='36' aria-label='Országhívó' required><option value='36'>+36</option></select>
                                <PiCaretDownBold aria-hidden='true' className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-500' />
                            </div>
                            <div className='relative'>
                                <select name='serviceProviderNumber' className={selectClassName + ' px-2 pr-7 text-center'} defaultValue='' aria-label='Körzetszám' required>
                                    <option value='' disabled>--</option>
                                    {[20, 30, 31, 50, 70].map((number) => <option key={number} value={number}>{number}</option>)}
                                </select>
                                <PiCaretDownBold aria-hidden='true' className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-500' />
                            </div>
                            <input id='phone-number' name='phoneNumber' type='tel' inputMode='numeric' pattern='[0-9]*' placeholder='Telefonszám' className={inputClassName} required />
                        </div>
                        <div className='relative'>
                            <PiEnvelopeSimpleDuotone className={inputIconClassName} />
                            <input id='email' name='email' type='email' autoComplete='email' placeholder='E-mail cím' className={iconInputClassName} required />
                        </div>

                        <div className='relative md:col-span-2'>
                            <PiChatCircleTextDuotone className='pointer-events-none absolute left-4 top-4 text-xl text-gray-400' />
                            <label htmlFor='message' className='sr-only'>Üzenet</label>
                            <textarea id='message' name='message' rows='5' placeholder='Miben segíthetünk?' className={iconInputClassName + ' resize-y'} required />
                        </div>

                        <label className='flex cursor-pointer items-start gap-3 rounded-xl bg-gray-50 p-4 md:col-span-2'>
                            <input type='checkbox' name='terms' className='mt-0.5 size-5 shrink-0 accent-blue-500' required />
                            <span className='text-xs leading-relaxed text-gray-600 sm:text-sm'>Az elküldés gombra kattintással elfogadod az <Link to='/adatkezelési-tájékoztató-diákoknak' target='_blank' className='font-semibold hover:underline'>Adatkezelési tájékoztatónkat</Link>.</span>
                        </label>

                        <label className={`flex items-center gap-4 rounded-xl border p-4 transition md:col-span-2 ${captchaState === 'checking' ? 'cursor-wait border-gray-200 bg-gray-50' : 'cursor-pointer'} ${captchaState === 'verified' ? 'border-green-300 bg-green-50/60' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`}>
                            <input
                                type='checkbox'
                                className='size-6 shrink-0 accent-green-600'
                                checked={captchaState !== 'idle'}
                                disabled={captchaState === 'checking'}
                                onChange={(event) => setCaptchaState(event.target.checked ? 'checking' : 'idle')}
                            />

                            <span className='min-w-0 flex-1'>
                                <span className='block text-sm font-semibold text-gray-700'>Nem vagyok robot</span>
                                <span className='block text-[11px] text-gray-500' aria-live='polite'>
                                    {captchaState === 'idle' && 'Jelöld be az ellenőrzéshez'}
                                    {captchaState === 'checking' && 'Ellenőrzés folyamatban…'}
                                    {captchaState === 'verified' && 'Sikeres ellenőrzés'}
                                </span>
                            </span>

                            <span className='flex w-14 shrink-0 flex-col items-center gap-1 text-center'>
                                {captchaState === 'checking'
                                    ? <span aria-hidden='true' className='size-7 animate-spin rounded-full border-[3px] border-blue-100 border-t-blue-500' />
                                    : captchaState === 'verified'
                                        ? <span aria-hidden='true' className='flex size-7 items-center justify-center rounded-full bg-green-500 text-base font-bold text-white'>✓</span>
                                        : <PiShieldCheckDuotone className='size-7 text-blue-500' />}
                                <span className='text-[8px] font-bold uppercase tracking-wide text-gray-400'>Check captcha</span>
                            </span>
                        </label>

                        {captchaState === 'verified' && <button type='submit' disabled={isSubmitting} className='gradient-yellow flex items-center justify-center gap-2 rounded-lg py-3.5 font-semibold text-white transition hover:-translate-y-0.5 hover:brightness-110 hover:shadow-xl active:translate-y-0 disabled:cursor-wait disabled:opacity-60 disabled:hover:translate-y-0 sm:py-4 md:col-span-2'>
                                <PiPaperPlaneTiltDuotone className='text-xl' />
                                {isSubmitting ? 'Küldés folyamatban…' : 'Elküldöm'}
                            </button>}
                    </div>

                </div>
            </form>
        </section>

        <FeedbackModal
            open={feedback !== null}
            onClose={closeFeedback}
            tone={feedback === 'success' ? 'success' : 'error'}
            title={feedback === 'success' ? 'Sikeres üzenetküldés!' : 'Valami nem stimmel!'}
            message={feedback === 'success'
                ? 'Kollégáink hamarosan keresni fognak az elérhetőségeiden, hogy válaszoljanak kérdésedre.'
                : 'Sajnos az üzenetedet nem tudtuk továbbítani. Kérjük, ellenőrizd az adatokat, és próbáld újra.'}
            actionLabel="Vissza"
        />
        </>
    );
}
